import { useEffect, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  query,
  where,
  onSnapshot,
  Timestamp,
} from "firebase/firestore";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import AdminHeader from "../components/AdminHeader";
import AdminSidebar from "../components/AdminSidebar";

export default function ActiveUsersPage() {
  const [count, setCount] = useState(0);
  const [hourlyHistory, setHourlyHistory] = useState([]);
  const [quarterHistory, setQuarterHistory] = useState([]);

  useEffect(() => {
    // 🔹 Live count (last 2 minutes, both guests + signed-in)
    const liveCutoff = new Date(Date.now() - 2 * 60 * 1000);
    const liveQuery = query(
      collection(db, "onlineUsers"),
      where("lastActive", ">", Timestamp.fromDate(liveCutoff))
    );
    const unsubLive = onSnapshot(liveQuery, (snap) => {
      setCount(snap.size);
    });

    // 🔹 Trend (last 24 hours)
    const trendCutoff = new Date(Date.now() - 24 * 60 * 60 * 1000);
    const trendQuery = query(
      collection(db, "onlineUsers"),
      where("lastActive", ">", Timestamp.fromDate(trendCutoff))
    );

    const unsubTrend = onSnapshot(trendQuery, (snap) => {
      const hourlyBuckets = {};
      const quarterBuckets = {};

      snap.forEach((doc) => {
        const d = doc.data();
        if (!d.lastActive) return;
        const ts = d.lastActive.toDate();

        const hourKey = ts.getHours();
        const quarter = Math.floor(ts.getMinutes() / 15) * 15;
        const quarterKey = `${String(ts.getHours()).padStart(2, "0")}:${String(
          quarter
        ).padStart(2, "0")}`;

        // Initialize buckets
        if (!hourlyBuckets[hourKey]) {
          hourlyBuckets[hourKey] = { signedIn: 0, guests: 0 };
        }
        if (!quarterBuckets[quarterKey]) {
          quarterBuckets[quarterKey] = { signedIn: 0, guests: 0 };
        }

        if (d.guest) {
          hourlyBuckets[hourKey].guests++;
          quarterBuckets[quarterKey].guests++;
        } else {
          hourlyBuckets[hourKey].signedIn++;
          quarterBuckets[quarterKey].signedIn++;
        }
      });

      // Build hourly data
      const hourlyData = [];
      for (let h = 0; h < 24; h++) {
        hourlyData.push({
          time: `${String(h).padStart(2, "0")}:00`,
          signedIn: hourlyBuckets[h]?.signedIn || 0,
          guests: hourlyBuckets[h]?.guests || 0,
        });
      }

      // Build quarter data (96 buckets per day)
      const quarterData = [];
      for (let h = 0; h < 24; h++) {
        for (let q of [0, 15, 30, 45]) {
          const key = `${String(h).padStart(2, "0")}:${String(q).padStart(2, "0")}`;
          quarterData.push({
            time: key,
            signedIn: quarterBuckets[key]?.signedIn || 0,
            guests: quarterBuckets[key]?.guests || 0,
          });
        }
      }

      setHourlyHistory(hourlyData);
      setQuarterHistory(quarterData);
    });

    return () => {
      unsubLive();
      unsubTrend();
    };
  }, []);

  return (
    <main className="min-h-screen bg-black text-white flex">
      {/* Sidebar */}
      <AdminSidebar />

      {/* Main content */}
      <div className="flex-1 flex flex-col">
        <AdminHeader />

        <div className="p-6 max-w-6xl mx-auto w-full space-y-6">
          {/* Page heading */}
          <header className="mb-6">
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-white">
              Active Users Tracker
            </h1>
            <p className="text-neutral-400 mt-1 text-sm">
              Live sessions now and activity trends split by signed-in vs guests.
            </p>
          </header>

          {/* Current count card */}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-6 text-center shadow-md">
            <p className="text-neutral-400 text-sm uppercase tracking-wide">
              Active Sessions Now
            </p>
            <p className="text-6xl font-extrabold text-[--neon] drop-shadow-[0_0_12px_rgba(212,255,0,.5)] mt-2">
              {count}
            </p>
          </div>

          {/* Hourly graph */}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-6 shadow-md">
            <h2 className="text-lg font-semibold mb-4 text-white">
              Activity (Hourly Buckets, Last 24h)
            </h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={hourlyHistory}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.1)" />
                <XAxis dataKey="time" stroke="#aaa" />
                <YAxis allowDecimals={false} stroke="#aaa" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#111",
                    border: "1px solid #333",
                    color: "#fff",
                  }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="signedIn"
                  name="Signed-in"
                  stroke="#4ade80" // green
                  strokeWidth={2.5}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="guests"
                  name="Guests"
                  stroke="#facc15" // yellow
                  strokeWidth={2.5}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* 15-minute graph */}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-6 shadow-md">
            <h2 className="text-lg font-semibold mb-4 text-white">
              Activity (15-Minute Buckets, Last 24h)
            </h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={quarterHistory}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.1)" />
                <XAxis
                  dataKey="time"
                  stroke="#aaa"
                  interval={23} // show ~1 label every 6 hours
                />
                <YAxis allowDecimals={false} stroke="#aaa" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#111",
                    border: "1px solid #333",
                    color: "#fff",
                  }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="signedIn"
                  name="Signed-in"
                  stroke="#4ade80" // green
                  strokeWidth={2.5}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="guests"
                  name="Guests"
                  stroke="#facc15" // yellow
                  strokeWidth={2.5}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </main>
  );
}
